import { isEmptyField } from 'usermgmt';
/**
 * ============================================================================
 *  PIM cache policy — what may be cached, and what makes it stale
 * ============================================================================
 * Every step of the wizard is destroyed the moment the user moves off it (see
 * the `@if (step.value === activeStep)` in pim-steps.html), so each step
 * re-fetches its rows from scratch on every single visit. Step 7 -> 6 -> 7 with
 * nothing typed costs three round trips for two screens of unchanged data.
 *
 * This file is the whole policy for fixing that, deliberately kept in ONE place
 * and free of Angular so it can be read (and unit-tested) end to end:
 *
 *   READ_RULES      which responses may be kept, and what each one DEPENDS ON
 *   WRITE_RULES     which of those dependencies a given save DIRTIES
 *   RESPONSE_RULES  which ANSWERS mean the server changed something on its own
 *
 * The connecting idea is a TAG. A cached response is stored under the tags of
 * the things it was built from ("the tags of station X", "asset row 42"). A
 * save announces the tags it just changed. Anything holding a dirtied tag is
 * thrown away. Nobody has to maintain a "saving step A also affects steps
 * B, C and D" table: a reader declares what it reads, a writer declares what it
 * writes, and the overlap is worked out at runtime.
 *
 * TWO RULES KEEP THIS HONEST — both chosen so that a mistake costs an extra
 * network call rather than showing somebody stale data:
 *
 *   1. A read nobody listed is NOT cached. Silence means "always ask the
 *      server".
 *   2. A write nobody listed empties the ENTIRE cache. Silence means "I have no
 *      idea what this changed, so trust nothing". This is why uploads and
 *      layout generation need no entry below: wiping everything is exactly
 *      right for them.
 *
 * Only non-GET requests can dirty anything, so plain GET reads never need an
 * entry. Several reads here ARE POSTs though (get_assets_by_filters,
 * get_divisions, ...), which is why the read rules are consulted first and a
 * matched read never counts as a write.
 */

/** A request, reduced to the three things the rules below actually look at. */
export interface PimRequestInfo {
  /** Path only — no origin, no query string. e.g. '/get_assets_by_filters'. */
  path: string;
  /** Upper case: 'GET', 'POST', ... */
  method: string;
  /** The request body. `null` for a GET. */
  body: any;
}

/**
 * How long a cached answer may live.
 *
 *   session - kept for as long as this session holds the draft's edit lock.
 *   never   - never cached. Listed anyway so the request is recognised as a
 *             read and does not fall through to "unknown write -> wipe
 *             everything".
 *
 * There is deliberately no "cache this regardless" scope. When the lock is not
 * held the whole layer is a pass-through, which is what makes the capability a
 * true off switch: the app is then byte-for-byte the app that existed before
 * this feature. The handful of responses PimStepsService already caches by hand
 * (the station record, its meta files, file blobs) keep doing exactly that,
 * untouched, in both states.
 */
export type PimCacheScope = 'session' | 'never';

/**
 * The tag vocabulary. Anything cached is filed under one or more of these, and
 * every save names the ones it dirtied. Both sides go through these helpers so
 * the two can never drift into spelling the same dependency differently.
 */
export const TAG = {
  /** Everything belonging to one station. Carried by every station-scoped entry,
   *  so "this whole station changed" (an upload, a re-extraction) is one call. */
  station: (stationUuid: string) => `station:${stationUuid}`,
  /** The station's own record, off GET /station/<uuid>. */
  stationRecord: (stationUuid: string) => `station-record:${stationUuid}`,
  /** The station's meta files — document paths, extracted blob urls. */
  stationMeta: (stationUuid: string) => `station-meta:${stationUuid}`,
  /** The list of documents uploaded against the station. */
  stationFiles: (stationUuid: string) => `station-files:${stationUuid}`,
  /** Asset rows of ONE type for ONE station — the per-step granularity. */
  asset: (assetTypeId: string, stationUuid: string) => `asset:${assetTypeId}@${stationUuid}`,
  /**
   * "Some asset row, somewhere, changed."
   *
   * Only for readers that cannot name the types they depend on — the
   * adjustment-point list is derived from whatever tracks currently exist, and
   * the global track list is not scoped to a station at all. Per-step reads
   * deliberately do NOT carry this, or every save would flush every step.
   */
  ANY_ASSET: 'asset:*',
  /** One asset row by its backend id. Lets a bounding-box save drop exactly the
   *  cached lists that contain that row, and nothing else. */
  row: (internalAssetId: string) => `row:${internalAssetId}`,
  /** Table-of-control rows. The save endpoint posts a bare array with no
   *  station on it, so TOC entries are dirtied as a group. */
  ANY_TOC: 'toc:*',
  /** The generated RFID layout of one station. */
  layout: (stationUuid: string) => `layout:${stationUuid}`,
  /** Backend master data that belongs to no station (zones, divisions, ...). */
  ref: (name: string) => `ref:${name}`
} as const;

/** A response worth keeping, and the dependencies that decide when to drop it. */
export interface PimReadRule {
  /** Shown in nothing; it is here so the rule reads as a sentence. */
  name: string;
  /** Left out = any method. */
  method?: string;
  match: RegExp;
  scope: PimCacheScope;
  /**
   * What this response was built from. Returning `null` means "I cannot work
   * out the dependencies for this particular request", and the response is then
   * not cached — never stale, just not saved. Ex: [station_uuid, asset_type_id]
   */
  tags?: (req: PimRequestInfo) => string[] | null;
  /** Extra tags only knowable once the answer is in — the row ids it carried. */
  responseTags?: (responseBody: any) => string[];
}

/** A save, and the tags it makes stale. */
export interface PimWriteRule {
  name: string;
  method?: string;
  match: RegExp;
  /** `[]` is a real answer: "this changes nothing anybody caches". */
  invalidates: (req: PimRequestInfo) => string[];
}

/** Anything to an array, for payload fields that are sometimes scalars. */
const toList = (value: any): any[] =>
  isEmptyField(value) ? [] : (Array.isArray(value) ? value : [value]);

/** The last path segment, which is where the parameterised urls carry their id. */
const lastSegment = (path: string): string => path.replace(/\/+$/, '').split('/').pop() ?? '';

/** Rows out of a GET_ASSET_BY_FILTERS response, whatever shape it came back in. */
const rowsOf = (body: any): any[] => {
  const result = body?.result;
  return Array.isArray(result) ? result : (Array.isArray(result?.data) ? result.data : []);
};

/**
 * Asset rows: one step's table. Depends on the station it was filtered by and
 * on the asset types it asked for — so saving Signals leaves the Dead Ends
 * table alone, while an upload (which dirties `station:`) drops both.
 *
 * The station-less variant is the GLOBAL track list, which spans every station
 * and can therefore only say "any asset at all".
 */
const assetFilterTags = ({ body }: PimRequestInfo): string[] => {
  const stationUuid = String(body?.station_uuid ?? '');
  if (!stationUuid) return [TAG.ANY_ASSET];
  return [
    TAG.station(stationUuid),
    ...toList(body?.asset_type_id).map((assetTypeId: any) => TAG.asset(String(assetTypeId), stationUuid))
  ];
};

/**
 * Tagging a cached list with the ids of the rows inside it is what lets a
 * bounding box — saved on its own endpoint the instant it is drawn, from deep
 * inside a shared child component — invalidate precisely the lists that show
 * that row. Without it, coming back to a step would redraw the box from before
 * the edit.
 */
const assetRowTags = (body: any): string[] =>
  rowsOf(body)
    .map((row: any) => row?.internal_asset_id)
    .filter(Boolean)
    .map((id: any) => TAG.row(String(id)));

/**
 * First match wins, so order matters where two patterns overlap. Everything not
 * listed here goes to the server every time.
 */
export const READ_RULES: readonly PimReadRule[] = [
  {
    name: 'asset rows for a step (GET_ASSET_BY_FILTERS)',
    method: 'POST',
    match: /\/get_assets_by_filters$/,
    scope: 'session',
    tags: assetFilterTags,
    responseTags: assetRowTags
  },
  {
    name: 'the station record (GET_STATION_BY_ID)',
    // PimStepsService also caches this by hand, so in practice this rule only
    // catches the callers that go straight to the endpoint.
    method: 'GET',
    match: /\/station\/[^/]+$/,
    scope: 'session',
    tags: ({ path }) => [TAG.station(lastSegment(path)), TAG.stationRecord(lastSegment(path))]
  },
  {
    name: 'station meta files (FETCH_SURVEY_META_FILES)',
    // Also cached by hand in PimStepsService — same note as above.
    method: 'POST',
    match: /\/get_station_meta_files$/,
    scope: 'session',
    tags: ({ body }) => [TAG.station(String(body?.station_uuid ?? '')), TAG.stationMeta(String(body?.station_uuid ?? ''))]
  },
  {
    name: 'documents uploaded against the station (GET_STATION_FILES)',
    method: 'POST',
    match: /\/station_details\/fetch_station_document_files$/,
    scope: 'session',
    tags: ({ body }) => [TAG.station(String(body?.station_uuid ?? '')), TAG.stationFiles(String(body?.station_uuid ?? ''))]
  },
  {
    name: 'survey files of the station (GET_SURVEY_FILES)',
    method: 'GET',
    match: /\/survey_file\/get_survey_files\/[^/]+$/,
    scope: 'session',
    tags: ({ path }) => [TAG.station(lastSegment(path)), TAG.stationFiles(lastSegment(path))]
  },
  {
    name: 'table-of-control rows (GET_TOC_DATA)',
    // '/toc/toc_data/<toc_type>/<station id>' — the ids ride in the url.
    method: 'GET',
    match: /\/toc\/toc_data\/[^/]+\/[^/]+$/,
    scope: 'session',
    tags: ({ path }) => [TAG.station(lastSegment(path)), TAG.ANY_TOC]
  },
  {
    name: 'per-track adjustment values (GET_TRACK_WITH_ADJUSTMENT)',
    // Derived from whatever tracks exist right now, and it cannot name the
    // tracks asset type from its own payload — so it declares the broad
    // "any asset changed" dependency and is dropped by any asset save.
    method: 'POST',
    match: /\/get_tracks_with_adjustments$/,
    scope: 'session',
    tags: ({ body }) => [TAG.station(String(body?.station_uuid ?? '')), TAG.ANY_ASSET]
  },
  {
    name: 'the layout file paths the Preview step opens with (GET_EACH_STATION_DATA)',
    method: 'POST',
    match: /\/get_each_station_data$/,
    scope: 'session',
    tags: ({ body }) => [TAG.station(String(body?.station_uuid ?? '')), TAG.layout(String(body?.station_uuid ?? ''))]
  },
  {
    name: 'the global track list (GET_TRACKS)',
    method: 'GET',
    match: /\/get_track_details$/,
    scope: 'session',
    tags: () => [TAG.ANY_ASSET]
  },
  {
    name: 'the station list (GET_STATIONS)',
    method: 'GET',
    match: /\/station$/,
    scope: 'session',
    tags: () => [TAG.ref('stations')]
  },
  {
    name: 'asset types (GET_ALL_ASSET_TYPES)',
    method: 'GET',
    match: /\/get_asset_types$/,
    scope: 'session',
    tags: () => [TAG.ref('asset-types')]
  },
  {
    name: 'document types (GET_DOCUMENT_TYPES)',
    method: 'GET',
    match: /\/get_document_types$/,
    scope: 'session',
    tags: () => [TAG.ref('document-types')]
  },
  {
    name: 'zones (GET_ZONES)',
    method: 'GET',
    match: /\/get_zones$/,
    scope: 'session',
    tags: () => [TAG.ref('zones')]
  },
  {
    name: 'divisions (GET_DIVISIONS)',
    method: 'POST',
    match: /\/get_divisions$/,
    scope: 'session',
    tags: () => [TAG.ref('divisions')]
  },
  {
    name: 'TOC templates (GET_TOC_TEMPLATES / GET_SIGNALING_TOC_TEMPLATES)',
    method: 'GET',
    match: /\/toc\/templates\/type\/[^/]+$/,
    scope: 'session',
    tags: () => [TAG.ref('toc-templates')]
  },

  // ---------------------------------------------------------------------
  //  Reads that must NEVER be cached. They are listed so that they are not
  //  mistaken for saves — a POST that only reads would otherwise wipe the
  //  whole cache on the "unknown write" rule.
  // ---------------------------------------------------------------------
  {
    name: 'file stream',
    // Two callers share this endpoint and they want opposite things:
    // PimStepsService.getFileStreamAsBlob caches blobs by path itself, while
    // the Preview step's streamJson() deliberately refuses to (a regenerated
    // layout is written back to the SAME path, so a cached read would hand back
    // the layout from before Generate). Nothing in the request distinguishes
    // them, so this layer stays out of it entirely and leaves both as they are.
    match: /\/stream_file(\/stream_file)?$/,
    scope: 'never'
  },
  {
    name: 'upload processing status poll',
    match: /\/station_details\/get_file_process_status$/,
    scope: 'never'
  },
  {
    name: 'draft lock status',
    match: /\/get_station_layout_lock_status$/,
    scope: 'never'
  },
  {
    name: 'the drafts list',
    match: /\/get_survey_drafts?$/,
    scope: 'never'
  }
];

/**
 * What each save makes stale. Anything not listed wipes the whole cache — see
 * the file header for why that is the right default.
 */
export const WRITE_RULES: readonly PimWriteRule[] = [
  {
    name: 'step rows saved (UPDATE_ASSET)',
    method: 'POST',
    match: /\/bulk_update_assets$/,
    invalidates: ({ body }) => {
      // Every row carries its own asset_type_id + station_uuid, so a save says
      // precisely which step's table it rewrote. New rows have no id yet, hence
      // the type tag as well as the row tags.
      // Annotated: TAG.ANY_ASSET is a literal type, so an inferred array
      // would refuse the built tags pushed in below.
      const tags: string[] = [TAG.ANY_ASSET];
      for (const update of toList(body?.updates)) {
        if (update?.asset_type_id) {
          tags.push(TAG.asset(String(update.asset_type_id), String(update.station_uuid ?? '')));
        }
        if (update?.internal_asset_id) {
          tags.push(TAG.row(String(update.internal_asset_id)));
        }
      }
      return tags;
    }
  },
  {
    name: 'rows deleted (BULK_DELETE_ASSETS)',
    method: 'POST',
    match: /\/bulk_delete_assets$/,
    // The payload names only the ids. Every cached list that contained one of
    // them carries its row tag, which is exactly the set that must go.
    invalidates: ({ body }) => [
      TAG.ANY_ASSET,
      ...toList(body?.internal_asset_ids).map((id: any) => TAG.row(String(id)))
    ]
  },
  {
    name: 'one row deleted (DELETE_ASSET)',
    match: /\/asset\/delete_asset\/[^/]+\/?$/,
    invalidates: ({ path }) => [TAG.ANY_ASSET, TAG.row(lastSegment(path))]
  },
  {
    name: 'a bounding box drawn or erased (BULK_UPDATE_BBOXES)',
    method: 'POST',
    match: /\/bulk_update_bboxes$/,
    // Only the rows whose rectangle moved. Deliberately NOT the broad asset
    // tag: drawing a box is a frequent, local act and must not cost every other
    // step its cached rows.
    invalidates: ({ body }) =>
      toList(body?.updates)
        .map((update: any) => update?.internal_asset_id)
        .filter(Boolean)
        .map((id: any) => TAG.row(String(id)))
  },
  {
    name: 'the station record saved (UPDATE_STATION)',
    method: 'POST',
    match: /\/station\/update_station$/,
    invalidates: ({ body }) => {
      const stationUuid = String(body?.station_uuid ?? body?.id ?? '');
      return [TAG.stationRecord(stationUuid), TAG.stationMeta(stationUuid), TAG.ref('stations')];
    }
  },
  {
    name: 'a station created (ADD_STATION)',
    method: 'POST',
    match: /\/station$/,
    invalidates: () => [TAG.ref('stations')]
  },
  {
    name: 'table-of-control saved (SAVE_TOC_DATA)',
    method: 'POST',
    match: /\/toc\/toc_data\/update\/?$/,
    invalidates: () => [TAG.ANY_TOC]
  },
  {
    name: 'the edit lock taken, refreshed or given back',
    // Housekeeping for the draft lock, including a heartbeat every three
    // minutes. It changes no data anybody reads, and without this entry the
    // heartbeat alone would empty the cache twenty times an hour.
    match: /\/(lock_station_layout|unlock_station_layout|refresh_station_layout_lock)$/,
    invalidates: () => []
  },
  {
    name: 'draft saved (SAVE_SURVEY_DRAFT)',
    // Writes the draft envelope, not the station's data. The drafts list it
    // does change is never cached (see the read rules above).
    method: 'POST',
    match: /\/save_survey_draft$/,
    invalidates: () => []
  }
];

/**
 * A read whose ANSWER can mean "the server changed something behind your back".
 *
 * The rules above all work off the REQUEST, which is enough whenever this tab
 * causes every change itself. Uploading a DXF is the one place that is not
 * true: the upload call returns immediately and the extraction runs afterwards
 * on a Celery worker (`dxf_script_task`), writing the extracted asset rows into
 * the database minutes later. No request from this tab causes those writes, so
 * no write rule can catch them.
 *
 * What the tab does have is the poll the Upload step already runs every ten
 * seconds while processing is in flight. The tick where `file_processed` turns
 * true IS the completion signal, so that is where the cache is emptied.
 *
 * It is read here rather than in upload.ts on purpose: the poll keeps running
 * even after the user has walked away from the Upload step, so watching the
 * response centrally catches the completion no matter which step is on screen.
 */
export interface PimResponseRule {
  name: string;
  match: RegExp;
  /** `null` = this answer changed nothing. `'all'` = empty the whole cache. */
  invalidates: (responseBody: any) => string[] | 'all' | null;
}

export const RESPONSE_RULES: readonly PimResponseRule[] = [
  {
    name: 'extraction finished on the server (GET_FILE_PROCESS_STATUS)',
    match: /\/station_details\/get_file_process_status$/,
    // Everything, because extraction rewrites the station wholesale: asset rows
    // of every type, the meta files, the document list. Naming tags here would
    // only be a slower way of being wrong. It fires once per upload, on the
    // single tick that flips to true — the false ticks cost nothing.
    invalidates: (body: any) => (body?.result?.data?.file_processed === true ? 'all' : null)
  }
];

const matches = (rule: { method?: string; match: RegExp }, req: PimRequestInfo): boolean =>
  (!rule.method || rule.method === req.method) && rule.match.test(req.path);

/** The rule describing this read, if it is one this layer knows about. */
export const matchRead = (req: PimRequestInfo): PimReadRule | undefined =>
  READ_RULES.find((rule) => matches(rule, req));

/** The rule describing this save. `undefined` means "unknown — wipe everything". */
export const matchWrite = (req: PimRequestInfo): PimWriteRule | undefined =>
  WRITE_RULES.find((rule) => matches(rule, req));

/** What this response says has gone stale, if this layer watches it at all. */
export const matchResponse = (req: PimRequestInfo): PimResponseRule | undefined =>
  RESPONSE_RULES.find((rule) => matches(rule, req));
